import { Redo, Undo } from "@mui/icons-material";
import { Box, Typography, useTheme } from "@mui/material";
import MDEditor, { commands, ICommand, MDEditorProps } from "@uiw/react-md-editor";
import { useState, useRef, useEffect, useMemo } from "react";
import { createMarkdownStyles } from "./styles";

type CustomMDEditorProps = {
    value: string;
    onChange: (value?: string) => void;
    undo?: () => void;
    redo?: () => void;
    customStyles?: object;
    placeholder?: string;
    error?: string | null | undefined;
} & MDEditorProps

export const CustomMDEditor = ({
    value,
    onChange,
    undo = () => { },
    redo = () => { },
    customStyles = {},
    placeholder = '',
    error = null,
    ...props
}: CustomMDEditorProps) => {
    const [editorHeight, setEditorHeight] = useState<string>("auto");
    const previewRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const markdownStyles = useMemo(() => createMarkdownStyles(theme), [theme]);

    useEffect(() => {
        const updateHeight = () => {
            if (previewRef.current) {
                const previewElement = previewRef.current.querySelector('.wmde-markdown');
                if (previewElement) {
                    const previewHeight = previewElement.clientHeight;
                    setEditorHeight(`${Math.max(previewHeight + 120, 400)}px`);
                }
            }
        };

        updateHeight();
        const observer = new ResizeObserver(updateHeight);
        if (previewRef.current) {
            observer.observe(previewRef.current);
        }

        return () => observer.disconnect();
    }, [value]);

    const extraCommands: ICommand[] = [
        {
            name: 'undo',
            keyCommand: 'undo',
            shortcuts: 'ctrl+z',
            icon: <Undo sx={{ fontSize: '12px' }} />,
            buttonProps: { 'aria-label': 'Undo', title: 'Undo (Ctrl+Z)' },
            execute: undo
        },
        {
            name: 'redo',
            keyCommand: 'redo',
            shortcuts: 'ctrl+shift+z',
            icon: <Redo sx={{ fontSize: '12px' }} />,
            buttonProps: { 'aria-label': 'Redo', title: 'Redo (Ctrl+Shift+Z)' },
            execute: redo
        },
    ];

    const placeholderStyles = placeholder && !value ? {
        '& .w-md-editor-text-input': {
            '&::placeholder': {
                color: theme.palette.text.disabled,
                opacity: 1,
            }
        }
    } : {};

    return (
        <>
            <Box
                ref={previewRef}
                sx={{
                    border: "1px solid",
                    borderColor: error ? "error.main" : "divider",
                    borderRadius: 1,
                    ...markdownStyles,
                    ...customStyles,
                    ...placeholderStyles,
                }}
            >
                <MDEditor
                    value={value}
                    onChange={onChange}
                    preview="live"
                    commands={[
                        ...extraCommands,
                        commands.divider,
                        ...commands.getCommands().filter(cmd => cmd.name !== 'checked-list'),
                    ]}
                    height={editorHeight}
                    visibleDragbar={false}
                    previewOptions={{
                        ...props.previewOptions,
                        className: 'editor-preview'
                    }}
                    textareaProps={{
                        placeholder: placeholder,
                    }}
                    {...props}
                />
            </Box>
            {error && (
                <Typography variant="caption" color="error">
                    {error}
                </Typography>
            )}
        </>
    );
};

export default CustomMDEditor;  
